/**
 * Frontend field restriction registry.
 * Single source of truth for mapping restriction keys → field name variants.
 * Mirrors the backend restrictable_fields.json structure.
 */

interface FieldRegistryEntry {
    id: string;
    label: string;
    category: string;
    fields: string[];
}

const FIELD_REGISTRY: FieldRegistryEntry[] = [
    {
        id: 'agent_pii',
        label: 'Agent',
        category: 'pii',
        fields: ['agent_first_name', 'agent_last_name', 'agent_full_info', 'agent_external_id', 'agent_active',
                 'agentFirstName', 'agentLastName', 'agentFullInfo', 'agentExternalId', 'agentActive',
                 'agentName', 'agent_name', 'assignee', 'assigneeAcronym',
                 'event_actors_full_info', 'event_actors_type', 'event_actors_contact_type'],
    },
    {
        id: 'customer_pii',
        label: 'Customer',
        category: 'pii',
        fields: ['customer_first_name', 'customer_last_name', 'customer_full_info', 'customer_external_id', 'customer_type',
                 'customerFirstName', 'customerLastName', 'customerFullInfo', 'customerExternalId', 'customerType',
                 'event_actors_full_info', 'event_actors_type', 'event_actors_contact_type'],
    },
    {
        id: 'team_pii',
        label: 'Team',
        category: 'pii',
        fields: ['team_name', 'team_shortname', 'teamName', 'teamShortname', 'agentTeamName'],
    },
];

const _keyToFields: Record<string, string[]> = {};
const _keyToEntry: Record<string, FieldRegistryEntry> = {};
const _fieldToKeys: Record<string, string[]> = {};
FIELD_REGISTRY.forEach(entry => {
    _keyToFields[entry.id] = entry.fields;
    _keyToEntry[entry.id] = entry;
    if (!_fieldToKeys[entry.id]) _fieldToKeys[entry.id] = [];
    if (!_fieldToKeys[entry.id].includes(entry.id)) _fieldToKeys[entry.id].push(entry.id);
    for (const field of entry.fields) {
        if (!_fieldToKeys[field]) _fieldToKeys[field] = [];
        if (!_fieldToKeys[field].includes(entry.id)) _fieldToKeys[field].push(entry.id);
    }
});

/**
 * Resolve restriction keys into a flat list of all field name variants.
 * This replaces the old `restrictedFields` array from /me.
 */
export function resolveRestrictedFields(restrictedFieldKeys: string[] | undefined | null): string[] {
    if (!restrictedFieldKeys || !restrictedFieldKeys.length) return [];
    const fields: string[] = [];
    for (const key of restrictedFieldKeys) {
        fields.push(key);
        const variants = _keyToFields[key];
        if (variants) {
            fields.push(...variants);
        }
    }
    return fields;
}

/**
 * Get detailed restriction info for the admin permissions UI.
 * This replaces the old `restrictedFieldsDetailed` array from /me.
 */
export function getRestrictedFieldsDetailed(restrictedFieldKeys: string[] | undefined | null): FieldRegistryEntry[] {
    if (!restrictedFieldKeys || !restrictedFieldKeys.length) return [];
    return restrictedFieldKeys
        .map(key => _keyToEntry[key])
        .filter(Boolean);
}

/**
 * Check if a specific field name is restricted, given a mixed array of
 * restriction keys and/or field name variants.
 * Works bidirectionally: resolves both fieldName and array items to their
 * group keys, so any combination of variants or keys resolves correctly.
 * Supports fields that belong to multiple groups (e.g. event_actors_full_info
 * belongs to both agent_pii and customer_pii).
 */
export function isFieldRestrictedByKeys(fieldName: string, restrictedFieldKeys: string[] | undefined | null): boolean {
    if (!restrictedFieldKeys || !restrictedFieldKeys.length) return false;
    if (restrictedFieldKeys.includes(fieldName)) return true;

    const fieldKeys = _fieldToKeys[fieldName];
    if (!fieldKeys || !fieldKeys.length) return false;

    for (const item of restrictedFieldKeys) {
        if (fieldKeys.includes(item)) return true;
        const itemKeys = _fieldToKeys[item];
        if (itemKeys && itemKeys.some(k => fieldKeys.includes(k))) return true;
    }
    return false;
}

export {FIELD_REGISTRY};
export type {FieldRegistryEntry};
